import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MultiMapContextService } from '@core/services/context/multimap-context.service';
import { ContextService } from '@core/services/core/context.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { AddressHelperService } from '@core/services/helpers/address-helper.service';
import { EventsHelperService } from '@core/services/helpers/events-helper.service';
import { NotificationHelperService } from '@core/services/helpers/notification-helper.service';
import { MultiMapService } from '@core/services/http-services/multimap.service';
import { AllFeatureFlags } from '@models/mmobile';
import {
  MultiMapAccessPoint,
  MultiMapError,
  MultiMapEvent,
  MultiMapLoginResponse,
  MultiMapNavigationEventData,
  MultiMapOperationRegisterEventData,
  MultiMapOperationRegisterEventId, MULTIMAP_EVENTS_NAMESPACE,
  MULTIMAP_ORIGIN_METS, MULTIMAP_ORIGIN_METS_WEB, MULTIMAP_PARAM_ORIGIN, MULTIMAP_PARAM_TOKEN
} from '@models/multimap';
import { CustomNotificationId } from '@models/notification-pg';
import { MultiMapOperation } from '@models/operation-register';
import { joinStrings } from '@utils/utils';
import { Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MultiMapHelperService {

  constructor(
    private mmobileHelper: MMobileHelperService,
    private notificationHelper: NotificationHelperService,
    private eventsHelper: EventsHelperService,
    private multiMapService: MultiMapService,
    private addressHelper: AddressHelperService,
    private or: OperationRegisterService,
    private multiMapContext: MultiMapContextService,
    private ctx: ContextService
  ) { }

  loadNotifications() {
    const multiMapIsEnabled = this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.MULTIMAP);
    if (!multiMapIsEnabled) {
      return;
    }
    this.notificationHelper.updateCustomNotification({ id: CustomNotificationId.NOTIFICATION_MULTIMAP });
  }

  getMultiMapToken(policyNumber?: string): Observable<MultiMapLoginResponse> {
    return this.loginMultiMap(policyNumber).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.error?.error?.error_code === MultiMapError.USER_NOT_FOUND) {
          return this.registerMultiMap(policyNumber);
        }
        throw err?.error;
      })
    );
  }

  private loginMultiMap(policyNumber?: string) {
    return this.multiMapService.getMultiMapLoginToken(policyNumber).pipe(
      switchMap(res => {
        return this.multiMapService.loginMultiMap(res.validationToken, res);
      })
    );
  }

  private registerMultiMap(policyNumber?: string) {
    return this.multiMapService.getMultiMapRegisterToken(policyNumber).pipe(
      switchMap(res => {
        return this.multiMapService.registerMultiMap(res.validationToken, res);
      })
    );
  }

  getMultiMapUrl(token: string) {
    const origin = this.ctx.isApp ? MULTIMAP_ORIGIN_METS : MULTIMAP_ORIGIN_METS_WEB;
    const params = new HttpParams().set(MULTIMAP_PARAM_TOKEN, token)
      .set(MULTIMAP_PARAM_ORIGIN, origin);
    const multiMapUrl: string = this.mmobileHelper.getCustomConfigData('URLS', 'MULTIMAP');
    const url = `${multiMapUrl}/integraciones/mets`;
    return `${url}?${params.toString()}`;
  }

  registerOperationFromIframe(data: MultiMapOperationRegisterEventData) {
    let operationCode;
    let structure = {};
    switch (data?.id) {
      case MultiMapOperationRegisterEventId.REQUEST_INIT:
        operationCode = MultiMapOperation.INICIO_SOLICITUD;
        break;
      case MultiMapOperationRegisterEventId.REQUEST_OK:
        operationCode = MultiMapOperation.SOLICITUD_PROFESIONAL;
        structure = data.data;
        break;
      case MultiMapOperationRegisterEventId.REQUEST_KO:
        operationCode = MultiMapOperation.ERROR_SOLICITUD_PROFESIONAL;
        structure = data.error;
        break;
      case MultiMapOperationRegisterEventId.REQUEST_LIST:
        operationCode = MultiMapOperation.INICIO_CONSULTA;
        structure = data.data;
        break;
      case MultiMapOperationRegisterEventId.REQUEST_LIST_KO:
        operationCode = MultiMapOperation.ERROR_SERVICIO_CONSULTA;
        structure = data.error;
        break;
      case MultiMapOperationRegisterEventId.REQUEST_DETAIL_OK:
        operationCode = MultiMapOperation.CONSULTA;
        structure = data.data;
        break;
      case MultiMapOperationRegisterEventId.REQUEST_SEND_MESSAGE_OK:
        operationCode = MultiMapOperation.ENVIO_MENSAJE;
        structure = data.data;
        break;
      case MultiMapOperationRegisterEventId.REQUEST_SEND_MESSAGE_KO:
        operationCode = MultiMapOperation.ERROR_MENSAJE;
        structure = data.error;
        break;
      default:
        return;
    }
    this.registerOperation(operationCode, structure);
  }

  registerOperation(
    operationCode: MultiMapOperation,
    structure: any = {}
  ) {
    const { accessPoint, policy } = this.multiMapContext.getState();
    let productId: string = accessPoint;
    if (accessPoint === MultiMapAccessPoint.PRODUCT && policy) {
      productId = this.addressHelper.getAddressResume(policy.address);
    }
    return this.or.registerMultiMapOperation(
      operationCode,
      productId,
      JSON.stringify(structure || {})
    );
  }

  getMultimapEvents() {
    const allowedOrigins = [(this.mmobileHelper.getCustomConfigData('URLS', 'MULTIMAP') || '')];
    return this.eventsHelper.getAllowedOriginPostMessages(allowedOrigins).pipe(
      map((event: MessageEvent) => this.parseMultiMapEvent(event)),
      filter((data: MultiMapEvent) => {
        return data?.namespace === MULTIMAP_EVENTS_NAMESPACE;
      })
    );
  }

  private parseMultiMapEvent(event: MessageEvent) {
    let data;
    try {
      data = JSON.parse(event?.data);
    } catch (e) { }
    return data as MultiMapEvent;
  }

  getAnalyticsSlug(stepProcess: string, stepName: string) {
    return joinStrings([stepProcess, stepName].filter(Boolean), ':');
  }

  getAnalyticsNavigationParams(data: MultiMapNavigationEventData, navigationsCounter: number) {
    // Add analytics dimensions in order
    const analyticsParams = {} as any;
    if (data?.errorType) {
      analyticsParams.CD115_error_type = data?.errorType;
    }
    if (data?.errorMessage) {
      analyticsParams.CD116_error_message = data?.errorMessage;
    }
    if (data?.stepProcess) {
      analyticsParams.CD131_process_type = data?.stepProcess;
    }
    if (data?.stepSection) {
      analyticsParams.CD132_process_name = data?.stepSection;
    }
    if (data?.stepNumber) {
      analyticsParams.CD133_process_step_number = data?.stepNumber;
    }
    if (data?.selectedCity) {
      analyticsParams.CD139_localidad = data?.selectedCity;
    }
    if (data?.selectedSpecialty) {
      analyticsParams.CD148_agrupacion = data?.selectedSpecialty;
    }
    if (data?.selectedProduct) {
      analyticsParams.CD149_servicio = data?.selectedProduct;
    }
    analyticsParams.CD178_process_progress_id = navigationsCounter;
    return analyticsParams;
  }
}
